import { useTranslation } from 'react-i18next';

import { isRecommendation, type Recommendation } from '@/api/vocabularies';
import { statusDotStyles } from '@/components/ui/statusDotStyles';
import { cn } from '@/lib/cn';
import { type } from '@/theme/typography';

interface Props {
  /** The interview's own overall recommendation — never an aggregate and never an interviewer's. */
  value: Recommendation;
}

/**
 * §6.7's **conclusion**: the overall recommendation of one submitted interview, at the size §6.2 gives
 * a dashboard metric (`DESIGN.md` §3, *Record verdict*).
 *
 * ⚠️ **The word, in the verdict's own colour, and no dot.** §3's rule is that a badge is a shape *and*
 * a word — the word is what carries the meaning and it is never dropped. At 48px the word **is** the
 * shape: a `size-1.5` dot beside it is a speck, and the colour stays what it always was, redundant to
 * the word rather than a second channel (§9).
 *
 * ⚠️ **It is this screen's rendering and not a change to the vocabulary.** `StatusDot` — the inline
 * dot/pill of §3's *Statuses and recommendations* — is untouched, and §6.2 Section 4, §6.3, §6.5 and
 * this screen's own *Other interviews* rows render exactly what they rendered. The colour is read from
 * `statusDotStyles`, which **is** that table, so the two cannot drift into disagreeing about what a
 * `NO` looks like.
 *
 * ⚠️ **ADR 0015 bounds it.** A verdict rendered large is **one interview's own verdict on its own
 * record**, which is what this screen is. It must never migrate to an aggregate or a per-interviewer
 * surface: a verdict keyed on the *interviewer* is the column ADR 0015 exists to refuse, and making it
 * large would be that refusal broken loudly.
 *
 * ⚠️ **A value outside the catalogue renders nothing rather than throwing**, which is the property
 * `StatusDot` records and this component inherits by asking the same `Record`: a badge that fails
 * silently is a bug, and a badge that throws takes the whole screen to §8's error state over one field.
 */
export function OverallVerdict({ value }: Props) {
  const { t } = useTranslation('components');

  const style = statusDotStyles[value];
  if (!isRecommendation(value) || style === undefined) return null;

  return <p className={cn(type.verdict, style.text)}>{t(`recommendation.${value}`)}</p>;
}
